import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { LuCode, LuPenLine } from "react-icons/lu";
import { TEMPLATE_VARIABLES } from "./api";

// The backend sanitises HTML on save, so the toolbar is deliberately limited to
// formatting that survives a conservative allowlist. Alignment/colour/font are
// left out because they rely on classes or inline styles that get stripped.
const MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

const FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "blockquote",
  "link",
];

// Quill ships its toolbar without tooltips, so label each control by hand.
const TOOLBAR_TITLES: Record<string, string> = {
  ".ql-header": "Text style — heading or normal paragraph",
  ".ql-bold": "Bold (Ctrl+B)",
  ".ql-italic": "Italic (Ctrl+I)",
  ".ql-underline": "Underline (Ctrl+U)",
  '.ql-list[value="ordered"]': "Numbered list",
  '.ql-list[value="bullet"]': "Bulleted list",
  ".ql-blockquote": "Quote block",
  ".ql-link": "Insert or edit a link",
  ".ql-clean": "Remove formatting from the selected text",
};

const VARIABLE_HINTS: Record<string, string> = {
  first_name: "The recipient's first name",
  app_download_url: "Link to download the app",
  app_url: "Link to the web app",
  catalog_url: "Link to the catalog",
};

interface Props {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
}

const RichTextEditor = ({ value, onChange, readOnly }: Props) => {
  const [sourceMode, setSourceMode] = useState(false);
  const quillRef = useRef<ReactQuill | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const modules = useMemo(() => MODULES, []);

  // The toolbar is rendered by Quill, so annotate its buttons once it exists.
  // Re-runs when leaving source mode, which remounts the editor.
  useEffect(() => {
    if (sourceMode) return;
    const toolbar = wrapRef.current?.querySelector(".ql-toolbar");
    if (!toolbar) return;
    Object.entries(TOOLBAR_TITLES).forEach(([selector, title]) => {
      toolbar
        .querySelectorAll(selector)
        .forEach((el) => el.setAttribute("title", title));
    });
  }, [sourceMode]);

  // Insert a {{variable}} at the caret (or append in source mode)
  const insertVariable = (name: string) => {
    const token = `{{${name}}}`;
    if (sourceMode) {
      onChange(`${value || ""}${token}`);
      return;
    }
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      onChange(`${value || ""}${token}`);
      return;
    }
    const range = editor.getSelection(true);
    const at = range ? range.index : editor.getLength();
    editor.insertText(at, token, "user");
    editor.setSelection(at + token.length, 0);
  };

  return (
    <div ref={wrapRef}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">Insert variable:</span>
          {TEMPLATE_VARIABLES.map((v) => (
            <button
              key={v}
              type="button"
              disabled={readOnly}
              title={VARIABLE_HINTS[v] || `Insert {{${v}}}`}
              onClick={() => insertVariable(v)}
              className="text-[11px] px-2 py-0.5 rounded border border-gray-200 text-[#0979A1] hover:bg-[#E8F7FA] disabled:opacity-50">
              {`{{${v}}}`}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSourceMode((s) => !s)}
          title={
            sourceMode
              ? "Back to the formatted editor"
              : "Edit the raw HTML behind this email"
          }
          className="flex items-center gap-1 text-[12px] text-gray-600 hover:text-[#0979A1]">
          {sourceMode ? <LuPenLine size={14} /> : <LuCode size={14} />}
          {sourceMode ? "Rich text" : "HTML source"}
        </button>
      </div>

      {sourceMode ? (
        <textarea
          value={value || ""}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className="w-full h-[320px] border border-[#0979A1] rounded-lg p-3 text-[12px] font-mono outline-none focus:border-[#0979A1]"
          placeholder="<p>Your email HTML…</p>"
        />
      ) : (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          readOnly={readOnly}
          onChange={onChange}
          modules={modules}
          formats={FORMATS}
          className="email-editor bg-white"
        />
      )}

      <p className="text-[11px] text-gray-400 mt-2">
        Only {TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(", ")} are allowed.
        The server sanitises HTML on save, so some styling may be stripped.
      </p>
    </div>
  );
};

export default RichTextEditor;
